import React, { useState, useContext } from 'react';
import { AuthContext, API } from '@/App';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { showToast } from '@/lib/custom-toast';
import { Eye, EyeOff, Mail, AlertCircle, Loader2 } from 'lucide-react';
import GoogleOAuthButton from '@/components/GoogleOAuthButton';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const emailParam = queryParams.get('email') || '';
  const isRegisterRoute = location.pathname === '/register' || queryParams.get('mode') === 'register' || Boolean(emailParam);

  const [isLogin, setIsLogin] = useState(!isRegisterRoute);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    username: emailParam ? emailParam.split('@')[0] : '',
    email: emailParam || '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerification = async (targetEmail) => {
    const emailToSend = targetEmail || unverifiedEmail || formData.email;
    if (!emailToSend) return;
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: emailToSend });
      showToast.success('Correo de verificación reenviado. Revisa tu bandeja de entrada y spam.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al reenviar el correo';
      showToast.error(msg);
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      if (!isLogin) {
        showToast.success('¡Cuenta creada! Revisa tu email para verificar tu cuenta.');
        setFormData({ username: '', email: '', password: '' });
        setUnverifiedEmail(null);
        setIsLogin(true);
      } else {
        await login(response.data.access_token, response.data.user);
        showToast.success('¡Bienvenido!');
        
        // En móviles y PWA (especialmente iOS), usar window.location es más confiable 
        // para asegurar que el estado de la sesión se refresque correctamente
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        
        if (window.Capacitor || isStandalone) {
          window.location.href = '/dashboard';
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error en la autenticación';
      const isUnverified = 
        errorMessage.toLowerCase().includes('no ha sido verificado') ||
        errorMessage.toLowerCase().includes('not verified') ||
        error.response?.status === 403;

      if (isUnverified) {
        setUnverifiedEmail(formData.email);
      }
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
      <Card className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/icon-192x192.png" 
              alt="PGastos" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h2>
            <p className="text-slate-600">
              {isLogin ? '¡Bienvenido de vuelta!' : 'Comienza gratis en segundos'}
            </p>
          </div>

          {/* Google OAuth Button */}
          <GoogleOAuthButton
            text={isLogin ? "Iniciar sesión con Google" : "Registrarse con Google"}
          />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">O continúa con</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="username">Nombre de Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="password">Contraseña</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-600 hover:text-slate-900"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              size="lg"
              disabled={loading}
            >
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta Gratis')}
            </Button>
          </form>

          {/* Alerta de correo no verificado con botón de reenvío */}
          {unverifiedEmail && (
            <div className="mt-5 p-4 bg-amber-50/90 border border-amber-200/90 rounded-2xl text-left animate-fadeIn">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-amber-900 leading-tight">
                    Cuenta registrada pendiente de verificación
                  </p>
                  <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                    El correo <span className="font-bold underline">{unverifiedEmail}</span> ya existe pero falta verificarlo. Revisa tu bandeja de entrada o solicita un nuevo enlace:
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleResendVerification(unverifiedEmail)}
                      disabled={resendingVerification}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold py-2 shadow-xs"
                    >
                      {resendingVerification ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Mail className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Reenviar correo de verificación
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(true);
                        setUnverifiedEmail(null);
                      }}
                      className="text-xs font-bold text-amber-800 hover:text-amber-950 px-2 py-1 underline transition-colors"
                    >
                      Ir a Iniciar Sesión
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setUnverifiedEmail(null);
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>
        </div>
      </Card>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPasswordModal} 
        onClose={() => setShowForgotPasswordModal(false)} 
      />

    </div>
  );
};

export default LoginPage;
